import React from "react";
import { Stack, Typography, Paper, Button, Divider } from "@mui/material";
import { styled } from "@mui/system";
import { AccountUsersRes, useSetDefaultUserMutation } from "src/store/features/api/generated";
import { UserManagementStyles } from "./styles/UserManagement.styles";

type Props = { user: AccountUsersRes[] | undefined; refetch: () => void; hideHeader?: boolean };

const DefaultButton = styled("div")(() => ({
  display: "flex",
  padding: "8px 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "20px",
  backgroundColor: "#F0FEF9",
  color: "#002035",
  fontFamily: "Roobert, sans-serif",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "20px",
  height: "36px",
  boxSizing: "border-box",
}));

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3337 4L6.00033 11.3333L2.66699 8"
      stroke="#002035"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserManagement: React.FC<Props> = (props) => {
  const { refetch, hideHeader } = props;
  const [setDefaultUser, { isLoading }] = useSetDefaultUserMutation();
  const classes = UserManagementStyles();
  const handleSetDefault = async (customerId: string) => {
    try {
      await setDefaultUser({ customerIdDto: { customerId } });
      refetch();
    } catch (error) {
      console.warn("Failed to set default user:");
    }
  };

  if (!props.user) {
    return <Typography>No users available.</Typography>;
  }

  return (
    <Paper
      className={classes.paperContainer}
      sx={
        hideHeader
          ? {
              padding: 0,
              borderRadius: 0,
              border: "none",
              backgroundColor: "transparent",
              boxShadow: "none",
            }
          : {}
      }>
      {!hideHeader && (
        <>
          <Typography className={classes.paperHeader}>User Management</Typography>
          <Typography className={classes.paperSubHeader}>
            Select a default account to be your primary account when you login.
          </Typography>
          <Divider className={classes.divider} />
        </>
      )}
      {props.user.map((user) => (
        <Stack
          key={user.customerId}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={classes.row}>
          <Stack direction="column" spacing={0.5}>
            <Typography className={classes.userTitle}>{user.name}</Typography>
            <Typography
              sx={{
                fontFamily: "Roobert, sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "20px",
                color: "#5C6469",
              }}>
              Customer ID: {user.customerId}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            {user.default ? (
              <DefaultButton>
                Default
                <CheckIcon />
              </DefaultButton>
            ) : (
              <Button
                disableRipple
                variant="contained"
                className={classes.setButton}
                onClick={() => handleSetDefault(user.customerId)}
                disabled={isLoading}>
                Set as default
              </Button>
            )}
          </Stack>
        </Stack>
      ))}
    </Paper>
  );
};

export default UserManagement;
